import { useEffect, useMemo, useState } from 'react'

import { BonusHighlightCard } from './components/BonusHighlightCard'
import { BonusModal } from '../BonusModal'
import { budgetPreviews } from './components/BudgetPreviews'
import { BudgetPreviewsWrapper } from '@components/shared/BudgetPreviewsWrapper'
import { Button } from '@components/shared/Button'
import { currencyMask } from '@utils/masks/currencyMask'
import { EditDetailedSummary } from './components/modals/EditDetailedSummary'
import { emotionTheme } from '@styles/theme'
import { initialPagination, type PaginationParams } from '@components/shared/Table/components/Pagination/types'
import { OcNfModal } from '../OcNfModal'
import { PageWrapper } from '@components/shared/PageWrapper'
import { parseBudgetPreviewsGrid } from './helpers/parseBudgetPreviewsGrid'
import { parseDetailedSummaryGrid } from './helpers/parseDetailedSummaryGrid'
import { parseDetailedSummaryTotalGrid } from './helpers/parseDetailedSummaryTotalGrid'
import { parseWeeklyBreakdownGrid } from './helpers/parseWeeklyBreakdownGrid'
import { PreviewWrapper } from '@components/shared/PreviewWrapper'
import { ReturnModal } from '@components/modals/ReturnModal'
import { RowTableTotal2 } from '@components/shared/RowTableTotal2'
import { Separator } from '@components/shared/Separator'
import { Slider } from '@components/shared/Slider'
import { Table } from '@components/shared/Table'
import { Title } from '@components/shared/Title'
import { useBonus } from '@pages/BonusModal/context'
import { useConsumption } from './context'
import { useMediaQuery } from '@mui/material'
import { useUser } from '@hooks/useUser'
import { PREVIEW_ACCOUNTS, PreviewHistoryDrawer } from '@components/shared/PreviewHistoryDrawer'
import { getPagePermissions } from '@hooks/useUser/provider'
// import { PROFILE_GU } from '@pages/Preview/helpers/previewGridValidationActions'
import { PageIds } from '@hooks/useLayout/types'
import { WeeklyBreakdownConsumptionFilters } from './components/WeeklyBreakdownConsumptionFilters'
import { WarningBanner } from '@components/shared/WarningBanner'
import { WarningIcon } from '@phosphor-icons/react'

import type { WeeklyBreakdownConsumptionFiltersFormProps } from './components/WeeklyBreakdownConsumptionFilters/form/types'
import type { DetailedSummaryV2ItemProps, WeeklyBreakdownItemProps } from './context/types'
import type { PermissionsItemProps } from '@pages/AccessProfile/context/types'
import type { SortProps } from '@components/shared/Table/types'

import * as S from './styles'

export const Consumption = () => {
  const isMobile = useMediaQuery(`(max-width: ${emotionTheme.screenSizes.laptop})`)

  const { filiData, userPermissionsData } = useUser()
  // const isGUProfile = userPermissionsData?.profile?.name === PROFILE_GU
  const {
    getDetailedSummary,
    getDetailedSummaryV2,
    detailedSummaryV2Data,
    isDetailedSummaryV2Loading,
    getWeeklyBreakdown,
    isWeeklyBreakdownLoading,
    weeklyBreakdownData,
    getBigNumbersConsumption,
    getConsumptionAveragePercentages,
    consumptionAveragePercentagesData,
    isBigNumbersConsumptionLoading,
    bigNumbersConsumptionData,
    isReleasePreviewConsumptionLoading,
    releasePreviewConsumption,
    isReversePreviewConsumptionLoading,
    reversePreviewConsumption
  } = useConsumption()
  const { bonusData, isBonusLoading, getBonus } = useBonus()

  const [openBonusModal, setOpenBonusModal] = useState(false)
  const [openOcNfDetailsModal, setOpenOcNfDetailsModal] = useState(false)
  const [openEditDetailedSummaryModal, setOpenEditDetailedSummaryModal] = useState(false)
  const [editDetailedSummaryData, setEditDetailedSummaryData] = useState<DetailedSummaryV2ItemProps | null>(null)
  const [weeklyBreakdownFormData, setWeeklyBreakdownFormData] =
    useState<WeeklyBreakdownConsumptionFiltersFormProps | null>(null)
  const [weeklyBreakdownSortData, setWeeklyBreakdownSortData] = useState<SortProps | null>(null)
  const [ocNfData, setOcNfData] = useState<WeeklyBreakdownItemProps | null>(null)
  const [openReleasePreviewModal, setOpenReleasePreviewModal] = useState(false)
  const [openReversePreviewModal, setOpenReversePreviewModal] = useState(false)
  const [permissions, setPermissions] = useState<PermissionsItemProps | null>(null)
  const [historyWeekNumber, setHistoryWeekNumber] = useState<number | null>(null)
  const canEditFromPreview = bigNumbersConsumptionData?.canEdit === true
  const canEditPage = canEditFromPreview && permissions?.canEdit === true
  const currentPreviewWeek = useMemo(() => {
    return (
      bigNumbersConsumptionData?.previews?.find((preview) => preview.isCurrentPreview)?.previewNumber ??
      bigNumbersConsumptionData?.currentPreviewNumber ??
      1
    )
  }, [bigNumbersConsumptionData?.currentPreviewNumber, bigNumbersConsumptionData?.previews])

  const handleOpenOcNf = (data: WeeklyBreakdownItemProps) => {
    setOpenOcNfDetailsModal(true)
    setOcNfData(data)
  }

  const handleOpenEditDetailedSummary = (v2Item: DetailedSummaryV2ItemProps) => {
    setOpenEditDetailedSummaryModal(true)
    setEditDetailedSummaryData(v2Item)
  }

  const handleGetWeeklyBreakdown = (
    params: PaginationParams | null,
    formData?: WeeklyBreakdownConsumptionFiltersFormProps | null,
    sortData?: SortProps
  ) => {
    getWeeklyBreakdown({
      ...(sortData || weeklyBreakdownSortData),
      ...(formData || weeklyBreakdownFormData),
      filiCode: filiData?.filiCode!,
      competenceDate: filiData?.startPeriod!,
      page: params?.page || initialPagination.PAGE,
      itemsPerPage: params?.itemsPerPage || weeklyBreakdownData?.itemsPerPage || initialPagination.ITEMS_PER_PAGE
    })
    !!formData && setWeeklyBreakdownFormData(formData)
    !!sortData && setWeeklyBreakdownSortData(sortData)
  }

  const handleInitialActionsFromScreen = () => {
    if (filiData?.filiCode && filiData?.filiCode !== 'ALL') {
      const payload = { filiCode: filiData?.filiCode!, competenceDate: filiData?.startPeriod! }

      handleGetWeeklyBreakdown(null)
      getDetailedSummary(payload)
      getDetailedSummaryV2(payload)
      getBigNumbersConsumption(payload)
      getConsumptionAveragePercentages(payload)
      getBonus(payload)
    }
  }

  useEffect(() => {
    handleInitialActionsFromScreen()
  }, [filiData])

  useEffect(() => {
    const pagePermissions = getPagePermissions(PageIds.CONSUMPTION)
    setPermissions(pagePermissions)
  }, [userPermissionsData])

  return (
    <>
      <PageWrapper
        breadcrumbData={[
          { label: 'Gestão Financeira' },
          { label: `Consumo / ${filiData?.filiCode} - ${filiData?.filiName}` }
        ]}
      >
        <WarningBanner message={bigNumbersConsumptionData?.corporateFoodSegmentMessage} />

        <Title label="Consumo" />

        <BudgetPreviewsWrapper>
          {isMobile ? (
            <Slider items={budgetPreviews({ data: bigNumbersConsumptionData })} />
          ) : (
            <PreviewWrapper>{budgetPreviews({ data: bigNumbersConsumptionData })}</PreviewWrapper>
          )}

          <Table
            data={parseBudgetPreviewsGrid({ data: bigNumbersConsumptionData, onViewHistory: setHistoryWeekNumber })}
            isLoading={isBigNumbersConsumptionLoading}
          />
        </BudgetPreviewsWrapper>

        <BonusHighlightCard
          data={bonusData}
          isLoading={isBonusLoading}
          onSeeBonus={() => setOpenBonusModal(true)}
        />
        <Title label="Resumo detalhado" />

        <Table
          data={parseDetailedSummaryGrid({
            data: detailedSummaryV2Data?.rows || [],
            averagePercentages: consumptionAveragePercentagesData,
            onEdit: handleOpenEditDetailedSummary,
            actionsDisabled: !canEditPage
          })}
          totalData={parseDetailedSummaryTotalGrid({
            total: detailedSummaryV2Data?.total
          })}
          fixedRow={{
            element: (
              <RowTableTotal2
                data={[
                  {
                    label: 'Total Consumo',
                    value: currencyMask(detailedSummaryV2Data?.totalConsumption?.toFixed(2)) || 0
                  }
                ]}
              />
            )
          }}
          isLoading={isDetailedSummaryV2Loading}
        />

        <Separator />

        <Title label="Detalhamento Semanal" marginBottom={16} />

        <WeeklyBreakdownConsumptionFilters onFilter={(formData) => handleGetWeeklyBreakdown(null, formData)} />

        <Table
          data={parseWeeklyBreakdownGrid({
            data: weeklyBreakdownData?.items || [],
            onViewMore: handleOpenOcNf
          })}
          fixedRow={{
            element: (
              <RowTableTotal2
                data={[
                  { label: 'Total Registros', value: weeklyBreakdownData?.totalItems || 0 },
                  // {
                  //   label: 'Total Consumo',
                  //   value: currencyMask(weeklyBreakdownData?.totalValue?.toFixed(2)) || 0
                  // },
                  {
                    label: 'Total OC',
                    value: currencyMask(weeklyBreakdownData?.totalValueOC?.toFixed(2)) || 0
                  }
                  // {
                  //   label: 'Total NF',
                  //   value: currencyMask(weeklyBreakdownData?.totalValueNF?.toFixed(2)) || 0
                  // },
                  // {
                  //   label: 'Total Transferido (OC)',
                  //   value: currencyMask(weeklyBreakdownData?.totalTransferred?.toFixed(2)) || 0
                  // }
                  // {
                  //   label: 'Total Transferido',
                  //   value: currencyMask(weeklyBreakdownData?.expectTotalAmount?.toFixed(2)) || 0
                  // }
                ]}
              />
            )
          }}
          footer={{
            pagination: {
              currentPage: weeklyBreakdownData?.currentPage || initialPagination.PAGE,
              itemsPerPage: weeklyBreakdownData?.itemsPerPage || initialPagination.ITEMS_PER_PAGE,
              totalItems: weeklyBreakdownData?.totalItems || 0,
              totalPages: weeklyBreakdownData?.totalPages || 0,
              onPageChange: (page) => handleGetWeeklyBreakdown({ page }),
              onItemsPerPageChange: (itemsPerPage) => handleGetWeeklyBreakdown({ itemsPerPage })
            }
          }}
          onSort={(sortData) => handleGetWeeklyBreakdown(null, null, sortData)}
          isLoading={isWeeklyBreakdownLoading}
        />

        <S.Buttons>
          {bigNumbersConsumptionData?.allowRevertPreview && (
            <Button
              width={isMobile ? '100%' : 190}
              colorType="outlineBlue"
              onClick={() => setOpenReversePreviewModal(true)}
            >
              Reverter Prévia
            </Button>
          )}
        </S.Buttons>

        {canEditPage && (
          <S.Buttons>
            {/* <Tooltip
              width="fit-content"
              title="Esse botão foi adicionado para facilitar os testes, após a validação da tela ele será removido"
              placement="top"
            >
              <Button
                width={isMobile ? '100%' : 190}
                colorType="outlineBlue"
                onClick={() =>
                  reverseWorkflowConsumption({ filiCode: filiData?.filiCode!, competenceDate: filiData?.startPeriod! })
                }
                disabled={
                  isReverseWorkflowConsumptionLoading ||
                  isBigNumbersConsumptionLoading ||
                  !(hasInProgressPreview || hasCompletedPreview)
                }
                loading={isReverseWorkflowConsumptionLoading}
              >
                Restaurar Prévia
              </Button>
            </Tooltip> */}

            {bigNumbersConsumptionData?.allowReleasePreview && (
              <Button
                width={isMobile ? '100%' : 190}
                colorType="solidBlue"
                onClick={() => setOpenReleasePreviewModal(true)}
              >
                Liberar Prévia
              </Button>
            )}
          </S.Buttons>
        )}
      </PageWrapper>

      <BonusModal open={openBonusModal} onClose={() => setOpenBonusModal(false)} />

      <OcNfModal
        data={ocNfData}
        open={openOcNfDetailsModal}
        onClose={() => setOpenOcNfDetailsModal(false)}
        canEdit={canEditPage}
      />

      <EditDetailedSummary
        data={editDetailedSummaryData}
        rows={detailedSummaryV2Data?.rows?.filter((r) => r.allowEdit) || []}
        open={openEditDetailedSummaryModal}
        onClose={() => setOpenEditDetailedSummaryModal(false)}
        canEdit={canEditPage}
      />

      <ReturnModal
        open={openReleasePreviewModal}
        headerIcon={<WarningIcon size={24} weight="bold" />}
        headerTitle="Liberar Prévia"
        message={`Tem certeza de que deseja liberar a ${currentPreviewWeek}ª Prévia de Consumo?`}
        cancelButtonText="Cancelar"
        confirmButtonText="Confirmar"
        onClose={setOpenReleasePreviewModal}
        isLoading={isReleasePreviewConsumptionLoading}
        onConfirm={() => {
          if (!canEditPage) return

          releasePreviewConsumption({
            filiCode: filiData?.filiCode!,
            competenceDate: filiData?.startPeriod!
          }).then((isSuccess) => isSuccess && setOpenReleasePreviewModal(false))
        }}
      />

      <ReturnModal
        open={openReversePreviewModal}
        headerIcon={<WarningIcon size={24} weight="bold" />}
        headerTitle="Reverter Prévia"
        message={`Tem certeza de que deseja reverter a ${currentPreviewWeek}ª Prévia de Consumo?`}
        cancelButtonText="Cancelar"
        confirmButtonText="Confirmar"
        onClose={setOpenReversePreviewModal}
        isLoading={isReversePreviewConsumptionLoading}
        onConfirm={() => {
          reversePreviewConsumption({
            filiCode: filiData?.filiCode!,
            competenceDate: filiData?.startPeriod!
          }).then((isSuccess) => isSuccess && setOpenReversePreviewModal(false))
        }}
      />

      <PreviewHistoryDrawer
        pageLabel="Consumo"
        accounts={[PREVIEW_ACCOUNTS.consumption, PREVIEW_ACCOUNTS.bonus]}
        weekNumber={historyWeekNumber}
        onClose={() => setHistoryWeekNumber(null)}
      />
    </>
  )
}
